import { formatCurrency } from '@/app/utils/formatCurrency';
import { FigureList, FigureRow } from './FigureList';
import { InvoiceOutcomeCard } from './InvoiceOutcomeCard';
import { PanelLinkAction } from './PanelLinkAction';
import { InvoiceSelectionLines } from './InvoiceSelectionLines';
import { AMOUNT_BASIS_LABELS } from '../utils/amountBasis';
import type { AmountBasis } from '../utils/amountBasis';
import { selectionAnnouncement } from '../utils/invoiceBalance';
import type { OutcomeCard } from '../utils/invoiceBalance';
import type { SelectionTotals } from '../utils/invoiceSelection';
import type { SelectionSummaryLine } from '../utils/selectionSummaryLines';

export interface InvoiceSelectionSectionProps {
    totals: SelectionTotals;
    summaryLines: SelectionSummaryLine[];
    lineMessages: Map<string, string>;
    onClear: () => void;
    availableBalance: string | null;
    outcome: OutcomeCard | null;
    locked: boolean;
    basis?: AmountBasis;
}

export function InvoiceSelectionSection({
    totals,
    summaryLines,
    lineMessages,
    onClear,
    availableBalance,
    outcome,
    locked,
    basis = 'net',
}: InvoiceSelectionSectionProps) {
    const count = summaryLines.length;
    const showFigures = count > 0 || availableBalance !== null;

    return (
        <section aria-labelledby="invoice-selection-heading" className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-2">
                <h3 id="invoice-selection-heading" className="text-sm font-semibold text-mp-foreground">
                    This invoice
                </h3>
                {count > 0 && (
                    <PanelLinkAction onClick={onClear} disabled={locked}>
                        Remove all
                    </PanelLinkAction>
                )}
            </div>

            <p aria-live="polite" className="sr-only">
                {selectionAnnouncement(count, totals[basis], outcome, availableBalance, basis)}
            </p>

            <InvoiceSelectionLines lines={summaryLines} lineMessages={lineMessages} basis={basis} />

            {showFigures && (
                <div className="border-t border-mp-border pt-3">
                    <FigureList>
                        {count > 0 && <FigureRow label={AMOUNT_BASIS_LABELS[basis].total} value={formatCurrency(totals[basis])} tone="emphasis" />}
                        {availableBalance !== null && (
                            <FigureRow label={AMOUNT_BASIS_LABELS[basis].balance} value={availableBalance} tone="muted" />
                        )}
                    </FigureList>
                </div>
            )}

            {outcome !== null && <InvoiceOutcomeCard outcome={outcome} />}
        </section>
    );
}
